"use client";

import { useEffect, useId, useRef, useState } from "react";

interface TutorSpeedMenuProps {
  min: number;
  max: number;
  step: number;
  value: number;
  isPending: boolean;
  error: string | null;
  onPreview: (value: number) => void;
  onCommit: (value: number) => void | Promise<void>;
}

/**
 * The tutor's speaking speed as one quiet header control ("1.0×") that opens a
 * small panel with the slider: it is rarely changed mid-lesson, so it need not
 * sit in the header as a permanent slider with its own labels.
 */
export function TutorSpeedMenu({
  min,
  max,
  step,
  value,
  isPending,
  error,
  onPreview,
  onCommit,
}: TutorSpeedMenuProps) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const formatted = `${value.toFixed(1)}×`;

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      // The panel takes this Escape; the session's own Escape (end session)
      // must not also fire.
      event.stopPropagation();
      setOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative" data-testid="tutor-speed-control">
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={`Tutor speaking speed, ${value.toFixed(1)} times`}
        title="Tutor speaking speed"
        onClick={() => setOpen((current) => !current)}
        className="flex h-11 min-w-11 items-center justify-center rounded-md px-2 text-[length:var(--session-meta-size)] tabular-nums text-[color:var(--session-meta-color)] transition-colors hover:bg-white/[0.06] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300/45"
      >
        {formatted}
      </button>
      {open ? (
        <div
          id={panelId}
          className="absolute right-0 top-[calc(100%+0.5rem)] z-40 w-56 rounded-lg border border-white/10 bg-[#121318] p-3 shadow-[0_18px_40px_rgb(0_0_0/0.45)]"
        >
          <label className="flex flex-col gap-2">
            <span className="flex items-center justify-between text-[length:var(--session-meta-size)] text-[color:var(--session-meta-color)]">
              <span className="font-semibold">Speaking speed</span>
              <span className="tabular-nums">{formatted}</span>
            </span>
            <input
              type="range"
              min={min}
              max={max}
              step={step}
              value={value}
              disabled={isPending}
              onChange={(event) => onPreview(Number(event.currentTarget.value))}
              onPointerUp={() => void onCommit(value)}
              onKeyUp={() => void onCommit(value)}
              onBlur={() => void onCommit(value)}
              className="tutor-speed-slider h-1 w-full cursor-pointer disabled:cursor-wait disabled:opacity-50"
              aria-label="Tutor speaking speed"
              aria-valuetext={`${value.toFixed(1)} times normal speed`}
            />
            <span className="text-[length:var(--session-meta-size)] text-[color:var(--session-meta-color)]">
              Applies to the tutor&apos;s next reply.
            </span>
          </label>
          {isPending ? (
            <span className="sr-only" role="status">
              Updating tutor speed
            </span>
          ) : null}
          {error ? (
            <p role="alert" className="mt-2 text-[length:var(--session-meta-size)] text-red-200">
              {error}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
